'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useDepartment, useDepartments } from '@/api/queries/useDepartments';
import { buildSortedFlatDepartmentList } from '@/lib/department-utils';
import { type Department } from '@bliks-io/contracts';
import {
  cn,
  Badge,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  appendDeletedSuffix,
  DELETED_ENTITY_CLASS
} from '@bliks-io/ui';
import { useTombstoneEntity } from '@/hooks/useTombstoneEntity';

export interface DepartmentSelectProps {
  /**
   * Selected department ID
   */
  value: string | undefined;

  /**
   * Change handler - receives department ID
   */
  onChange: (departmentId: string | undefined) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Additional class for trigger button
   */
  className?: string;

  /**
   * Department ID to exclude from the list (useful for parent selection)
   */
  excludeId?: string;

  /**
   * Field ID for accessibility
   */
  id?: string;

  /**
   * Allow clearing the selection (useful for filters)
   */
  allowClear?: boolean;

  /**
   * Custom label for the clear/all option
   */
  clearLabel?: string;

  /**
   * Optional callback for custom clear behavior. If not provided, defaults to onChange(undefined)
   */
  onClear?: () => void;

  /**
   * If provided, restricts selection to only this department ID
   */
  restrictToDepartmentId?: string;

  /**
   * If provided, restricts selection to only these department IDs
   */
  restrictToDepartmentIds?: string[];

  /**
   * Show the current value even when it's not in the fetched options
   * (soft-deleted department) — grayed out in the trigger and appended as a
   * disabled, non-reselectable item at the bottom of the list. Edit forms
   * enable this so an existing assignment never renders as a blank field;
   * filters keep it off.
   */
  showUnselectableValue?: boolean;
}

interface DepartmentWithPrefix {
  department: Department;
  prefix?: string;
}

/**
 * Base DepartmentSelect component - controlled component using value/onChange
 * Uses Combobox with tree structure display and search
 * Can be used with useState or as a building block for form components
 *
 * @example
 * ```tsx
 * const [department, setDepartment] = useState<string | undefined>();
 *
 * <DepartmentSelect
 *   value={department}
 *   onChange={setDepartment}
 *   placeholder="Select department"
 * />
 * ```
 */
export const DepartmentSelect = React.forwardRef<HTMLButtonElement, DepartmentSelectProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      excludeId,
      className,
      id,
      allowClear = false,
      clearLabel,
      onClear,
      restrictToDepartmentId,
      restrictToDepartmentIds,
      showUnselectableValue = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { data: departments, isLoading, isError } = useDepartments();

    // Soft-deleted current value, resolved via the detail endpoint so edit
    // forms can still display it
    const tombstoneDepartment = useTombstoneEntity(
      showUnselectableValue,
      value,
      departments,
      useDepartment
    );

    const [searchQuery, setSearchQuery] = React.useState('');

    const isDisabled = disabled || isLoading || isError || !departments;

    // Filter out excluded department and build tree structure
    const sortedFlatDepartments = React.useMemo<DepartmentWithPrefix[]>(() => {
      if (!departments) return [];

      let availableDepartments = excludeId
        ? departments.filter((dept) => dept._id !== excludeId)
        : departments;

      // If restricted to specific department(s), filter the list
      if (restrictToDepartmentId) {
        availableDepartments = availableDepartments.filter(
          (dept) => dept._id === restrictToDepartmentId
        );
      } else if (restrictToDepartmentIds && restrictToDepartmentIds.length > 0) {
        availableDepartments = availableDepartments.filter((dept) =>
          restrictToDepartmentIds.includes(dept._id)
        );
      }

      return buildSortedFlatDepartmentList(availableDepartments);
    }, [departments, excludeId, restrictToDepartmentId, restrictToDepartmentIds]);

    // Determine placeholder text based on state
    let placeholderText = placeholder || t('departmentSelect.placeholder');
    if (isLoading) {
      placeholderText = t('departmentSelect.loading');
    } else if (isError || !departments) {
      placeholderText = t('departmentSelect.error');
    } else if (sortedFlatDepartments.length === 0) {
      placeholderText = t('departmentSelect.noDepartments');
    }

    // Get selected department for trigger display — fall back to the resolved
    // tombstone so a deleted current value still renders instead of a blank field
    const selectedDepartment = React.useMemo(() => {
      if (!departments || !value) return null;
      return departments.find((dept) => dept._id === value) ?? tombstoneDepartment;
    }, [departments, value, tombstoneDepartment]);

    // Filter departments by search query
    const filteredDepartments = React.useMemo(() => {
      if (!searchQuery) return sortedFlatDepartments;

      const q = searchQuery.toLowerCase();
      return sortedFlatDepartments.filter((item) => {
        const dept = item.department;
        const name = dept.name.toLowerCase();
        const description = dept.description?.toLowerCase() || '';
        const abbreviation = dept.abbreviation?.toLowerCase() || '';

        return name.includes(q) || description.includes(q) || abbreviation.includes(q);
      });
    }, [sortedFlatDepartments, searchQuery]);

    // Render trigger content with badge
    const triggerContent = React.useMemo(() => {
      if (!selectedDepartment) {
        return null;
      }

      const isDeleted = Boolean(selectedDepartment.deletedAt);

      return (
        <div
          className={cn(
            'flex items-center justify-between gap-2 flex-1 min-w-0',
            isDeleted && DELETED_ENTITY_CLASS
          )}
        >
          <span className="truncate">
            {appendDeletedSuffix(selectedDepartment.name, isDeleted, t)}
          </span>
          {selectedDepartment.abbreviation && (
            <Badge variant="secondary" className="ml-auto shrink-0 text-white">
              {selectedDepartment.abbreviation}
            </Badge>
          )}
        </div>
      );
    }, [selectedDepartment, t]);

    // Handle value change - ensure single value
    const handleValueChange = React.useCallback(
      (newValue: string | string[] | undefined) => {
        if (typeof newValue === 'string') {
          onChange(newValue);
        } else {
          onChange(undefined);
        }
      },
      [onChange]
    );

    // Handle clear option selection
    const handleClearSelect = React.useCallback(() => {
      if (onClear) {
        onClear();
      } else {
        onChange(undefined);
      }
    }, [onClear, onChange]);

    return (
      <Combobox
        value={value}
        onValueChange={handleValueChange}
        disabled={isDisabled}
        onClear={onClear}
      >
        <ComboboxTrigger
          ref={ref}
          id={id}
          placeholder={placeholderText}
          allowClear={allowClear}
          className={className}
        >
          {triggerContent}
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput
            placeholder={t('departmentSelect.search')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <ComboboxList>
            <ComboboxEmpty>{t('departmentSelect.noResults')}</ComboboxEmpty>
            {allowClear && (
              <ComboboxItem value="__clear__" onSelect={handleClearSelect} className="gap-2">
                <span className={cn('flex-1', !value && 'font-semibold')}>
                  {clearLabel || t('departmentSelect.allDepartments')}
                </span>
                <Check className={cn('h-4 w-4 shrink-0', !value ? 'opacity-100' : 'opacity-0')} />
              </ComboboxItem>
            )}
            {filteredDepartments.map((item) => {
              const dept = item.department;
              const prefix = item.prefix;

              return (
                <ComboboxItem key={dept._id} value={dept._id} className="gap-2">
                  <span className="flex-1 flex items-center gap-2 min-w-0">
                    {prefix && (
                      <span className="font-mono text-muted-foreground/50 whitespace-pre">
                        {prefix}
                      </span>
                    )}
                    <span className="truncate min-w-0">{dept.name}</span>
                    {dept.abbreviation && (
                      <Badge variant="secondary" className="ml-auto shrink-0 text-white">
                        {dept.abbreviation}
                      </Badge>
                    )}
                  </span>
                </ComboboxItem>
              );
            })}
            {/* Current value that's deleted — visible but not reselectable (no tree prefix) */}
            {tombstoneDepartment && (
              <ComboboxItem value={tombstoneDepartment._id} disabled className="gap-2">
                <span
                  className={cn('flex-1 flex items-center gap-2 min-w-0', DELETED_ENTITY_CLASS)}
                >
                  <span className="truncate min-w-0">
                    {appendDeletedSuffix(
                      tombstoneDepartment.name,
                      Boolean(tombstoneDepartment.deletedAt),
                      t
                    )}
                  </span>
                  {tombstoneDepartment.abbreviation && (
                    <Badge variant="secondary" className="ml-auto shrink-0 text-white">
                      {tombstoneDepartment.abbreviation}
                    </Badge>
                  )}
                </span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }
);

DepartmentSelect.displayName = 'DepartmentSelect';
